<template>
  <div class="health">
    <a-spin :spinning="loading">
      <div class="head">
        <h3 class="head-left">基本信息</h3>
        <div class="head-right">
          <span class="serialNumber" v-if="!isMedicationAdvice">
            编号{{ info.base.serialNumber }}
          </span>
          <span>开方时间{{ info.base.createTime }}</span>
        </div>
      </div>
      <!-- 病历信息 -->
      <ul class="detail_info">
        <auth-img
          v-if="getStatusImg(info.base) && getStatusImg(info.base).src"
          class="detail_info-status"
          :src="getStatusImg(info.base).src"
          :alt="getStatusImg(info.base).alt"
          style="width: 120px"
        />
        <li
          class="detail_info-item"
          v-for="column in columnsBase"
          :class="(column.class || []).join(' ')"
          :key="column"
        >
          <label class="item_label">{{ column.label }}</label>
          <span class="item_content">
            <span v-if="['chineseDiagnoses', 'westernDiagnoses'].includes(column.prop)">
              {{ renderValue(info.base[column.prop]) }}
            </span>
            <span v-else-if="column.prop === 'patientName'">
              <PatientNameAgeReveal
                v-if="isRealName"
                :name="info.base.patientName"
                :age="info.base.patientAge"
              />
              <span v-else>{{ column.render(info.base) }}</span>
            </span>
            <span v-else-if="column.prop === 'patientAge'">
              {{ column.render(info.base) }}
            </span>
            <!-- 复诊信息 -->
            <span v-else-if="column.prop === 'revisitInfo'">
              <span v-if="info.base.revisitInfoVO?.revisitTime">
                {{ info.base.revisitInfoVO?.revisitTime }}
                ( {{ info.base.revisitInfoVO?.revisitType === 1 ? '线上问诊' : '门诊就诊' }})
              </span>
              <span v-else>--</span>
            </span>
            <span v-else>{{ info.base[column.prop] || '--' }}</span>
          </span>
        </li>
      </ul>
      <div v-for="c in prescriptionDetailVOList" :key="c.serialNumber">
        <DrugTable
          :title="'药品信息'"
          :info="c"
          :isShowInfoBtn="!isMedicationAdvice"
          :isCHINESE="c.prescriptionType === 2"
          :source="c.medicineDetailVOList"
          :instanceBusinessType="c.prescriptionBusinessType"
          @prescription="emit('jumpTo', 'prescriptionOnline', '0', c.serialNumber)"
        />
      </div>
      <!-- //辅助方 -->
      <DrugTable
        :title="'商品信息'"
        v-if="healthPrescriptionDetailVO"
        linkTitle="查看健康辅助方详情"
        :isCHINESE="false"
        :isHealthDrug="true"
        :isShowInfoBtn="!isMedicationAdvice"
        :source="healthPrescriptionDetailVO.medicineDetailVOList"
        @prescription="
          emit('jumpTo', 'prescriptionHealth', '0', healthPrescriptionDetailVO.serialNumber)
        "
      />
      <div class="detail_drugs">
        <div class="amount-row main">
          <span class="label">总价</span>
          <span class="value">{{ info.base.summationAmount ?? '--' }}元</span>
        </div>
        <div class="amount-row" v-if="info.base.cureServiceAmount">
          <span class="label">诊疗费</span>
          <span class="value">{{ info.base.cureServiceAmount || '--' }}元</span>
        </div>
        <div class="amount-row" v-if="info.base.medicineAmount">
          <span class="label">医药费</span>
          <span class="value">{{ info.base.medicineAmount || '--' }}元</span>
        </div>
      </div>
    </a-spin>
  </div>
</template>
<script setup>
import { uniteDetail } from '@/api/workPlace/prescription'
import { BASE_LABEL, getStatusImg } from './config'
import { defineProps, defineEmits, ref, reactive, computed, onMounted } from 'vue'
import DrugTable from './DrugTable.vue'
import PatientNameAgeReveal from '@/components/PatientNameAgeReveal.vue'

const prop = defineProps({
  // 病历编号
  serialNumber: {
    type: String,
    default: ''
  },
  // 病历编号
  id: {
    type: [String, Number],
    default: '0'
  }
})
const emit = defineEmits('noDetail')
// 渲染基本信息
const columnsBase = ref([...BASE_LABEL])
// 渲染数据
const info = reactive({
  // 基本信息
  base: {}
})
//处方实例集合
const prescriptionDetailVOList = ref([])
//辅助方实例
const healthPrescriptionDetailVO = ref(null)
const loading = ref(false)
const isMedicationAdvice = ref(false)
// 是否真实患者姓名（用药建议等场景为「待完善」，不展示小眼睛）
const isRealName = computed(() => {
  const name = info.base.patientName
  return !!name && name !== '待完善'
})
// 联合处方详情
async function groupDetail() {
  loading.value = true
  info.base = {}
  info.base.patientGender = ''
  try {
    const result = await uniteDetail(prop.serialNumber)
    // 是否是用药建议
    isMedicationAdvice.value = result.unionPrescriptionInnerType === 2
    info.base = result
    info.base.patientGender = result.patientGender === 1 ? '男' : '女'
    prescriptionDetailVOList.value = result.prescriptionDetailVOList || []
    healthPrescriptionDetailVO.value = result.healthPrescriptionDetailVO || null
    loading.value = false
    // 如果是用药建议就显示待完善
    const isAdbiceText = '待完善'
    if (isMedicationAdvice.value) {
      info.base.patientName = isAdbiceText
      info.base.patientGender = isAdbiceText
      info.base.patientAge = isAdbiceText
    }
  } catch (error) {
    loading.value = false
    emit('noDetail', '', prop.serialNumber)
  }
}

const renderValue = list => {
  if (!list) return '--'
  return list.map(item => item.name).join(',')
}
onMounted(() => {
  groupDetail()
})
</script>

<style lang="scss" scoped>
.health {
  .head {
    display: flex;
    justify-content: space-between;

    .head-left {
      font-weight: bold;
      font-size: 16px;
      color: #333333;
    }

    .head-right {
      color: #999999;
      font-weight: 400;
      font-size: 14px;

      span:nth-child(1) {
        margin-right: 40px;
      }
    }
  }

  .detail_info {
    display: grid;
    grid-template-columns: repeat(3, 33.33%);
    grid-template-rows: repeat(2, minmax(44px, auto));
    list-style: none;
    margin: 0;
    padding-left: 0;
    grid-gap: 5px 0;
    position: relative;
    margin-bottom: 30px;

    &-status {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1;
      transform: translateY(-30px);
    }

    &-item {
      padding: 20px 0 20px 16px;
      display: flex;
      align-items: center;
      border-bottom: 1px solid #e8e8e8;

      .item_label {
        flex-shrink: 0;
        color: #999999;
        margin-right: 3px;
      }

      .item_content {
        flex: auto;
        width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        position: relative;
        font-weight: bold;

        &:after {
          content: '';
          display: block;
          width: 1px;
          background: #e8e8e8;
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          margin: auto;
        }
      }

      &.noBorder {
        .item_content:after {
          background: transparent;
        }
      }
      &.noPadding {
        padding-left: 0;
      }
      &.middle {
        grid-column: 1/3;
      }
    }

    // 网格布局大
    .big {
      grid-column-start: 2;
      grid-column-end: 4;
    }

    // 网格布局 巨大
    .large {
      grid-column-start: 1;
      grid-column-end: 4;
    }

    // 网格布局 小
  }

  // 底部金额信息
  .detail_drugs {
    // 金额区靠右展示，占容器右侧约一半宽度
    width: 25%;
    margin-left: auto;
    padding: 10px 0;

    .amount-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      line-height: 28px;

      .label {
        color: #999999;
        font-size: 14px;
        font-weight: 400;
      }

      .value {
        color: #333333;
        font-size: 14px;
        font-weight: bold;
      }

      // 总价强调展示
      &.main {
        .label {
          color: #333333;
          font-weight: bold;
        }

        .value {
          font-size: 18px;
        }
      }
    }
  }
}
.comment-body {
  color: black;
}
</style>
